import {z} from "zod"

export const float32Array = z.instanceof(Float32Array)

export const uint32Array = z.instanceof(Uint32Array)

export const dataObjectReference = z.object({
    type: z.literal("dataObjectReference"),
    dataObjectId: z.number().or(z.string()),
})

export const vector2Like = z.object({
    x: z.number(),
    y: z.number(),
})

export const vector3Like = z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
})

export const vector4Like = z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
    w: z.number(),
})

export const paperLikeMatrix = z.object({
    a: z.number(),
    b: z.number(),
    c: z.number(),
    d: z.number(),
    tx: z.number(),
    ty: z.number(),
})

export const matrix3x2Like = z.union([
    z.object({
        elements: z.array(z.number()),
    }),
    paperLikeMatrix,
    z.tuple([z.number(), z.number(), z.number(), z.number(), z.number(), z.number()]),
])

export const matrix4Like = z.object({
    m11: z.number(),
    m12: z.number(),
    m13: z.number(),
    m14: z.number(),
    m21: z.number(),
    m22: z.number(),
    m23: z.number(),
    m24: z.number(),
    m31: z.number(),
    m32: z.number(),
    m33: z.number(),
    m34: z.number(),
    m41: z.number(),
    m42: z.number(),
    m43: z.number(),
    m44: z.number(),
})

export const size2Like = z.object({
    width: z.number(),
    height: z.number(),
})

export const box2Like = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
})

export const colorLike = z.object({
    r: z.number(),
    g: z.number(),
    b: z.number(),
    a: z.number().optional(),
})

export const remotelyColorLike = z.union([colorLike, z.array(z.number()), z.number()])

export const DataType = z.union([z.literal("uint8"), z.literal("uint8norm"), z.literal("uint8norm_srgb"), z.literal("float16"), z.literal("float32")])
export type DataType = z.infer<typeof DataType>

export const ChannelLayout = z.union([z.literal("R"), z.literal("RGB"), z.literal("RGBA")])
export type ChannelLayout = z.infer<typeof ChannelLayout>

export const AddressMode = z.union([z.literal("border"), z.literal("clamp"), z.literal("wrap"), z.literal("wrap-mirrored")])
export type AddressMode = z.infer<typeof AddressMode>

export const halPainterParameterNumber1 = z.union([z.literal("float"), z.literal("int"), z.literal("uint")])
export const halPainterParameterNumber2 = z.union([z.literal("float2"), z.literal("int2"), z.literal("uint2")])
export const halPainterParameterNumber3 = z.union([z.literal("float3"), z.literal("int3"), z.literal("uint3")])
export const halPainterParameterNumber4 = z.union([z.literal("float4"), z.literal("int4"), z.literal("uint4")])
export const halPainterParameterNumber1Array = z.union([z.literal("float[]"), z.literal("int[]"), z.literal("uint[]")])
export const halPainterParameterNumber2Array = z.union([z.literal("float2[]"), z.literal("int2[]"), z.literal("uint2[]")])
export const halPainterParameterNumber3Array = z.union([z.literal("float3[]"), z.literal("int3[]"), z.literal("uint3[]")])
export const halPainterParameterNumber4Array = z.union([z.literal("float4[]"), z.literal("int4[]"), z.literal("uint4[]")])

export const halPainterParameterValueTypeNumber1 = z.object({type: halPainterParameterNumber1.readonly(), value: z.number()})
export const halPainterParameterValueTypeNumber2 = z.object({type: halPainterParameterNumber2.readonly(), value: vector2Like})
export const halPainterParameterValueTypeNumber3 = z.object({type: halPainterParameterNumber3.readonly(), value: vector3Like})
export const halPainterParameterValueTypeNumber4 = z.object({type: halPainterParameterNumber4.readonly(), value: vector4Like})
export const halPainterParameterValueTypeNumber1Array = z.object({type: halPainterParameterNumber1Array.readonly(), value: z.array(z.number())})
export const halPainterParameterValueTypeNumber2Array = z.object({type: halPainterParameterNumber2Array.readonly(), value: z.array(vector2Like)})
export const halPainterParameterValueTypeNumber3Array = z.object({type: halPainterParameterNumber3Array.readonly(), value: z.array(vector3Like)})
export const halPainterParameterValueTypeNumber4Array = z.object({type: halPainterParameterNumber4Array.readonly(), value: z.array(vector4Like)})
export const halPainterParameterValueTypeMatrix3x2 = z.object({type: z.literal("float3x2").readonly(), value: matrix3x2Like})
export const halPainterParameterValueTypeMatrix4x4 = z.object({type: z.literal("float4x4").readonly(), value: matrix4Like})

export const halPainterParameterValueType = z.union([
    halPainterParameterValueTypeNumber1,
    halPainterParameterValueTypeNumber2,
    halPainterParameterValueTypeNumber3,
    halPainterParameterValueTypeNumber4,
    halPainterParameterValueTypeNumber1Array,
    halPainterParameterValueTypeNumber2Array,
    halPainterParameterValueTypeNumber3Array,
    halPainterParameterValueTypeNumber4Array,
    halPainterParameterValueTypeMatrix3x2,
    halPainterParameterValueTypeMatrix4x4,
])

export const halPainterParameter = z.union([
    halPainterParameterValueType,
    z.object({
        isOptional: z.boolean().optional().default(false),
    }),
])

export const halPainterParameters = z.record(z.string().readonly(), halPainterParameter)
